/**
 * StaffPicker — PILOT-26
 * Dropdown + button to assign a staff member to a request.
 */

import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '@/config/api';
import { getToken } from '@/staff/api/authApi';

interface StaffMember {
  id: string;
  name: string;
  role: string;
}

interface StaffPickerProps {
  requestId: string;
  /** Staff name stored in the request's assignedTo field */
  currentAssigneeName: string | null;
  onAssigned: () => void;
  disabled?: boolean;
}

export function StaffPicker({
  requestId,
  currentAssigneeName,
  onAssigned,
  disabled = false,
}: StaffPickerProps) {
  const [roster, setRoster] = useState<StaffMember[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rosterError, setRosterError] = useState(false);

  const fetchRoster = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/requests/staff`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setRosterError(true);
        return;
      }
      const data: StaffMember[] = await res.json();
      setRoster(data);
    } catch {
      setRosterError(true);
    }
  }, []);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const handleAssign = async () => {
    if (!selectedId) return;
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/requests/${requestId}/assign`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ staffId: selectedId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError((body as { error?: string }).error ?? 'Assignment failed');
        return;
      }
      setSelectedId('');
      onAssigned();
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  if (rosterError) {
    return (
      <p className="text-xs text-slate-500 italic">Staff roster unavailable</p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {currentAssigneeName && (
        <p className="text-xs text-slate-400">
          Currently assigned to{' '}
          <span className="font-medium text-white">{currentAssigneeName}</span>
        </p>
      )}

      <div className="flex gap-2 items-center flex-wrap">
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          disabled={disabled || loading || roster.length === 0}
          className="flex-1 min-w-0 bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          <option value="">
            {roster.length === 0 ? 'Loading staff…' : currentAssigneeName ? 'Reassign to…' : 'Assign to…'}
          </option>
          {roster.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.role})
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleAssign}
          disabled={disabled || loading || !selectedId}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Assigning…' : 'Assign'}
        </button>
      </div>

      {error && (
        <p className="text-xs text-rose-400">{error}</p>
      )}
    </div>
  );
}
